<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/navigation';

import MainPhotosNewsItem from './MainPhotosNewsItem.vue';
import type { IPhotosItemDataClientDTO } from '~/shared/api/client/photos/photos.dto';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';

const props = defineProps<{ items: IPhotosItemDataClientDTO[] }>();

const slides = ref<IPhotosItemDataClientDTO[]>(props.items);

const photosList = computed(() => slides.value.slice(1, 4));

const swiperRef = ref();
const swiperKey = ref(0);

function swapWithFirst(index: number) {
  const currentSlides = slides.value;
  if (index === 0 || index >= currentSlides.length) return;

  const first = currentSlides[0];
  const target = currentSlides[index];

  if (!first || !target) return;

  currentSlides[0] = target;
  currentSlides[index] = first;

  swiperKey.value++;
}

const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});

onMounted(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    };
  }
});
</script>

<template>
  <div v-if="items.length > 0" :class="$style.photos">
    <div :class="$style.photos__header">
      <div :class="$style.photos__headerTitle">Фотографии</div>
      <Button
        theme="outline"
        size="small"
        to="https://cre.ru/events/reports/photos"
        :class="$style.photos__headerButton"
      >
        Смотреть все
      </Button>
    </div>
    <div :class="$style.photos__slider">
      <Swiper
        v-if="navigationOptions.prevEl && navigationOptions.nextEl"
        ref="swiperRef"
        :key="swiperKey"
        :class="$style.photos__slider__swiperContainer"
        :grab-cursor="true"
        :loop="true"
        :modules="[Navigation]"
        :navigation="navigationOptions"
      >
        <SwiperSlide
          v-for="(item, key) in slides[0]?.gallery.photos"
          :key="`mainPhotosSlider_${key}`"
          :class="$style.photos__slide"
        >
          <img
            :src="resizeImage(item.photo.file_url, { width: 1080 })"
            :srcset="
              resizeImageSrcsetWidths(
                item.photo.file_url,
                [480, 720, 1080, 1440, 2160],
              )
            "
            sizes="(max-width: 767px) 100vw, (max-width: 1379px) 70vw, 1080px"
            alt="slide"
            :class="$style.photos__slider__photo"
          />
        </SwiperSlide>
      </Swiper>

      <div
        :class="$style.photos__slider__controls"
        :style="{
          '--arrows-color': '#111111',
        }"
      >
        <div
          ref="prevEl"
          :class="[
            'custom-button-prev',
            $style.photos__slider__controlsItem,
            $style.photos__slider__controlsItemPrev,
          ]"
        >
          <Icon
            :class="$style.photos__slider__controlsIcon"
            name="my-icon:i-arrow-left"
          />
        </div>
        <div
          ref="nextEl"
          :class="[
            'custom-button-next',
            $style.photos__slider__controlsItem,
            $style.photos__slider__controlsItemNext,
          ]"
        >
          <Icon
            :class="$style.photos__slider__controlsIcon"
            name="my-icon:i-arrow-right"
          />
        </div>
      </div>

      <div :class="$style.photos__info">
        <div :class="$style.photos__info__header">Фото дня</div>
        <div :class="$style.photos__info__title">
          {{ slides[0]?.title }}
        </div>
      </div>
    </div>

    <div :class="$style.photos__list">
      <MainPhotosNewsItem
        v-for="(item, key) in photosList"
        :key="`mainPhotosItem_${key}`"
        :class="$style.photos__list__item"
        :photo="item.gallery.photos?.[0]!"
        :title="item.title!"
        @click="swapWithFirst(key + 1)"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.photos {
  background: var(--color-gray-bg);
  border-bottom: 1px solid var(--color-gray-line);

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: 64px;
    padding-left: var(--space-2-5x);
    padding-right: var(--space-2-5x);
    gap: var(--space-2-5x);
    justify-content: space-between;
    border-bottom: 1px solid var(--color-gray-line);

    &Title {
      @include text_h5;
    }

    &Button {
      height: 40px;
    }
  }

  &__slider {
    padding: var(--space-2-5x);
    position: relative;

    &__swiperContainer {
      margin-bottom: var(--space-1-5x);
    }

    &__controls {
      display: flex;
      flex-flow: row nowrap;
      position: absolute;
      right: 0;
      top: 515px;
      z-index: 1;

      path {
        fill: var(--arrows-color);
      }

      &Item {
        display: flex;
        width: 100px;
        height: 64px;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        background-color: var(--color-white);

        &Next {
          border-left: 1px solid var(--color-gray-line);
        }
      }
    }

    &__photo {
      width: 100%;
      height: auto;
      max-height: 527px;
      object-fit: cover;
      object-position: center;
    }
  }

  &_slide {
    display: flex;
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);
    &__header {
      @include text_caption2_caps;
      color: var(--color-gray-text);
    }
    &__title {
      @include text_h5;
      color: var(--color-black-1);
      padding-right: 200px;
    }
  }

  &__list {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    border-top: 1px solid var(--color-gray-line);

    &__item {
      padding: var(--space-2-5x);
      border-right: 1px solid var(--color-gray-line);
      cursor: pointer;
      &:last-child {
        border-right: none;
      }
    }
  }
}

@media (hover: hover) {
  .photos__slider__controlsItem {
    transition: all 0.2s;

    &:hover {
      background-color: var(--color-gray-line);
    }
  }
}

@media (max-width: 1379px) {
  .photos {
    &__info {
      &__title {
        padding-right: 0;
      }
    }
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .photos {
    &__slider {
      &__photo {
        max-height: 368px;
      }
      &__controls {
        top: 356px;
      }
    }
  }
}

@media (max-width: 767px) {
  .photos {
    &__slider {
      &__photo {
        max-height: 236px;
      }
      &__controls {
        top: 224px;
      }
    }
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .photos {
    &__list {
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: inherit;

      &__item {
        border-bottom: 1px solid var(--color-gray-line);
        &:last-child {
          grid-column: span 2;
          width: 100%;
        }
      }
    }
  }
}

@media (min-width: 0px) and (max-width: 599px) {
  .photos {
    &__slider {
      &__controls {
        bottom: 73px;
      }

      &__swiperContainer {
        margin-bottom: var(--space-5x);
      }
    }
  }
}

@media (max-width: 479px) {
  .photos {
    &__header {
      border-bottom: none;
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
    }

    &__slider {
      padding-top: 0;
      padding-bottom: var(--space-2x);

      &__swiperContainer {
        margin-left: -20px;
        margin-right: -20px;
      }
      &__photo {
        height: 220px;
      }
      &__controls {
        top: 188px;
      }
    }

    &__list {
      @include mobileSliderWrapper;

      &__item {
        @include mobileSliderItem;
      }
    }
  }
}
</style>
